import React from "react";

/**
 * Performance optimization utilities
 * Reusable performance helpers for the application
 */

/**
 * Throttle function to limit execution frequency
 * @param {Function} func - Function to throttle
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Throttled function
 */
export const throttle = (func, delay) => {
  let timeoutId;
  let lastExecTime = 0;
  return function (...args) {
    const currentTime = Date.now();

    if (currentTime - lastExecTime > delay) {
      func.apply(this, args);
      lastExecTime = currentTime;
    } else {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(
        () => {
          func.apply(this, args);
          lastExecTime = Date.now();
        },
        delay - (currentTime - lastExecTime)
      );
    }
  };
};

/**
 * Debounce function to delay execution until after calls have stopped
 * @param {Function} func - Function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, delay) => {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
};

/**
 * Cache factory for avoiding repeated expensive operations
 * @param {number} duration - Cache duration in milliseconds
 * @returns {Object} Cache object with get and set methods
 */
export const createCache = (duration = 5 * 60 * 1000) => {
  const cache = new Map();

  return {
    get: (key) => {
      const item = cache.get(key);
      if (!item) return null;

      if (Date.now() - item.timestamp > duration) {
        cache.delete(key);
        return null;
      }

      return item.data;
    },
    set: (key, data) => {
      cache.set(key, {
        data,
        timestamp: Date.now(),
      });
    },
    clear: () => cache.clear(),
    size: () => cache.size,
  };
};

/**
 * Intersection Observer hook for lazy loading
 * @param {Object} options - Intersection Observer options
 * @returns {Array} [ref, isIntersecting]
 */
export const useIntersectionObserver = (options = {}) => {
  const [isIntersecting, setIsIntersecting] = React.useState(false);
  const [ref, setRef] = React.useState(null);

  React.useEffect(() => {
    if (!ref) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
        if (entry.isIntersecting && options.once !== false) {
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: "50px",
        ...options,
      }
    );

    observer.observe(ref);
    return () => observer.disconnect();
  }, [ref, options]);

  return [setRef, isIntersecting];
};

/**
 * Performance measurement utility
 * @param {string} name - Performance mark name
 * @param {Function} fn - Function to measure
 * @returns {Promise} Result of the function
 */
export const measurePerformance = async (name, fn) => {
  const startMark = `${name}-start`;
  const endMark = `${name}-end`;
  const measureName = `${name}-measure`;

  performance.mark(startMark);

  try {
    const result = await fn();
    performance.mark(endMark);
    performance.measure(measureName, startMark, endMark);

    if (process.env.NODE_ENV === "development") {
      const measure = performance.getEntriesByName(measureName)[0];
      console.log(`⚡ ${name} took ${measure.duration.toFixed(2)}ms`);
    }

    return result;
  } catch (error) {
    performance.mark(endMark);
    performance.measure(measureName, startMark, endMark);
    throw error;
  } finally {
    // Clean up performance entries
    performance.clearMarks(startMark);
    performance.clearMarks(endMark);
    performance.clearMeasures(measureName);
  }
};

/**
 * Image preloader utility
 * @param {Array<string>} imageUrls - Array of image URLs to preload
 * @returns {Promise<Array>} Promise that resolves when all images are loaded
 */
export const preloadImages = (imageUrls) => {
  return Promise.all(
    imageUrls.map((url) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(url);
        img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
        img.src = url;
      });
    })
  );
};

/**
 * Bundle size checker (development only)
 */
export const logBundleInfo = () => {
  if (process.env.NODE_ENV !== "development") return;

  if ("connection" in navigator) {
    const connection = navigator.connection;
    console.log("🌐 Network Info:", {
      effectiveType: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt,
    });
  }

  // Log performance timing
  window.addEventListener("load", () => {
    const perfData = performance.getEntriesByType("navigation")[0];
    console.log("📊 Page Load Performance:", {
      domContentLoaded: perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart,
      loadComplete: perfData.loadEventEnd - perfData.loadEventStart,
      totalTime: perfData.loadEventEnd - perfData.fetchStart,
    });
  });
};

/**
 * Memory usage monitor (development only)
 */
export const monitorMemoryUsage = () => {
  if (process.env.NODE_ENV !== "development" || !performance.memory) return;

  const logMemory = () => {
    const memory = performance.memory;
    console.log("🧠 Memory Usage:", {
      used: `${(memory.usedJSHeapSize / 1024 / 1024).toFixed(2)} MB`,
      total: `${(memory.totalJSHeapSize / 1024 / 1024).toFixed(2)} MB`,
      limit: `${(memory.jsHeapSizeLimit / 1024 / 1024).toFixed(2)} MB`,
    });
  };

  // Log memory usage every 30 seconds
  setInterval(logMemory, 30000);
  logMemory(); // Initial log
};
